/**
 * Asks the Properties panel's action section to bring one component event into view — from outside the
 * panel (the canvas selection bar's event chip). The panel may not be mounted yet when the request is
 * made (the caller switches the right panel to Properties first), or a mounted picker may belong to
 * another host, so a request no listener HANDLES is kept for the next subscriber
 * (`consumePendingOpenEventRequest`) — briefly: a stale request must not open a picker much later.
 */
export interface IOpenEventRequest {
  kuikaId: string;
  eventName: string;
  /** `add` also opens the add-action picker; `edit` only scrolls to and highlights the event's actions. */
  mode: "add" | "edit";
}

/** Returns true when it handled the request (it is that component's screen-designer action section). */
export type OpenEventListener = (request: IOpenEventRequest) => boolean;

/** A request nobody handled is dropped after this long. */
export const PENDING_OPEN_EVENT_TTL_MS: number = 2000;

interface IPendingRequest {
  request: IOpenEventRequest;
  at: number;
}

const listeners: Set<OpenEventListener> = new Set<OpenEventListener>();
let pendingRequest: IPendingRequest | undefined;

export const subscribeOpenEventRequest = (listener: OpenEventListener): (() => void) => {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
};

export const requestOpenEventInPanel = (request: IOpenEventRequest): void => {
  let isHandled: boolean = false;
  listeners.forEach((listener: OpenEventListener): void => {
    isHandled = listener(request) || isHandled;
  });
  pendingRequest = isHandled ? undefined : { request, at: Date.now() };
};

export const consumePendingOpenEventRequest = (): IOpenEventRequest | undefined => {
  const pending: IPendingRequest | undefined = pendingRequest;
  pendingRequest = undefined;
  if (!pending || Date.now() - pending.at > PENDING_OPEN_EVENT_TTL_MS) {
    return undefined;
  }
  return pending.request;
};

/** Test-only: forget listeners and any pending request. */
export const resetOpenEventRequests = (): void => {
  listeners.clear();
  pendingRequest = undefined;
};
